import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu } from '@kerfjs/ui/popup-menu';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import type { SafeHtml } from 'kerfjs';
import { MoreHorizontal } from 'lucide';

import {
  editorSegment,
  libraryDocs,
  libraryTab,
  selectedDocumentIds,
  selectedLibraryDocIds,
  selectedLineIds,
} from '../../../data/state.js';
import { menuProps, type MenuTrigger } from '../../../lib/action-menu.js';
import { demandReadOnly, isRequester } from '../../../lib/context.js';
import { DocumentActionsMenu } from '../../demand-editor/components/document-actions-menu.js';
import { SelectionActionsMenu } from '../../demand-editor/components/selection-actions-menu.js';
import {
  LibraryDocumentActionsMenu,
  selectedLibraryDocuments,
} from '../../library/components/library-document-actions-menu.js';

/** Selection command menu shared by selected line items and documents. */
export function SelectionActionsGroup({ view: v }: { view: string }): SafeHtml {
  if (v === 'library' && libraryTab.value === 'documents') return <LibraryDocumentsActionsGroup />;
  const onDemand = (v === 'workspace' || v === 'intake') && !isRequester();
  if (!onDemand || demandReadOnly()) return <></>;
  const onLines = editorSegment.value === 'lines';
  const onDocuments = editorSegment.value === 'files';
  const count = onDocuments ? selectedDocumentIds.value.length : selectedLineIds.value.length;
  const disabled = (!onLines && !onDocuments) || count === 0;
  const label =
    !onLines && !onDocuments
      ? 'Selection Actions — open Line Items or Documents to select'
      : count === 0
        ? `Selection Actions — select ${onDocuments ? 'files' : 'line items'} first`
        : `Actions for ${String(count)} Selected ${onDocuments ? `File${count === 1 ? '' : 's'}` : `Line${count === 1 ? '' : 's'}`}`;
  const menu = (trigger: MenuTrigger): SafeHtml =>
    onDocuments ? (
      <DocumentActionsMenu count={count} trigger={trigger} />
    ) : (
      <SelectionActionsMenu count={count} trigger={trigger} />
    );
  return (
    <>
      <ToolbarControlGroup
        label="Selection Actions"
        single={true}
        nestedDropdown={true}
        className="selection-actions-group"
      >
        {disabled ? (
          <PopupMenu
            {...menuProps(
              {
                label,
                icon: <LucideIcon icon={MoreHorizontal} name="" />,
                disabled: true,
                rootAttributes: { 'data-selection-menu': '' },
              },
              [],
            )}
          />
        ) : (
          menu({
            label,
            icon: <LucideIcon icon={MoreHorizontal} name="" />,
            rootAttributes: { 'data-selection-menu': '' },
          })
        )}
      </ToolbarControlGroup>
    </>
  );
}

/** The Knowledge Base Documents tab's "…" menu for the selected documents (HS2-KRH1MW). */
function LibraryDocumentsActionsGroup(): SafeHtml {
  const selected = selectedLibraryDocuments(libraryDocs.value, selectedLibraryDocIds.value);
  const count = selected.length;
  const trigger: MenuTrigger = {
    label:
      count === 0
        ? 'Document actions — select documents first'
        : `Actions for ${String(count)} selected document${count === 1 ? '' : 's'}`,
    icon: <LucideIcon icon={MoreHorizontal} name="" />,
    disabled: count === 0,
    rootAttributes: { 'data-selection-menu': '' },
  };
  return (
    <ToolbarControlGroup
      label="Selection Actions"
      single={true}
      nestedDropdown={true}
      className="selection-actions-group"
    >
      {count === 0 ? (
        <PopupMenu {...menuProps(trigger, [])} />
      ) : (
        <LibraryDocumentActionsMenu selected={selected} trigger={trigger} />
      )}
    </ToolbarControlGroup>
  );
}
